import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslations } from 'use-intl';

import { PageHeader } from '@/components/dashboard/page-header';
import { RatingForm } from '@/components/rating/rating-form';
import { ReceivedRating } from '@/components/rating/received-rating';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/projects/$id/rating')({
  component: RatingPage,
});

function RatingPage(): React.ReactElement {
  const { id } = Route.useParams();
  const t = useTranslations('rating');
  const meQuery = trpc.user.me.useQuery();
  const projectQuery = trpc.project.byId.useQuery({ projectId: id });
  const ratingQuery = trpc.rating.byProject.useQuery({ projectId: id });

  const header = (
    <>
      <div>
        <Link
          to="/projects/$id"
          params={{ id }}
          className="text-muted-foreground hover:text-foreground text-sm transition-colors"
        >
          ← {t('backToProject')}
        </Link>
      </div>
      <PageHeader title={t('pageTitle')} subtitle={t('pageSubtitle')} />
    </>
  );

  if (meQuery.isLoading || projectQuery.isLoading || ratingQuery.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <p className="text-muted-foreground text-sm">{t('loading')}</p>
      </div>
    );
  }
  if (projectQuery.isError || !projectQuery.data || !meQuery.data) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <div className="border-border bg-card/50 rounded-2xl border border-dashed p-10 text-center">
          <p className="text-muted-foreground text-sm">{t('error')}</p>
        </div>
      </div>
    );
  }

  const project = projectQuery.data;
  const userId = meQuery.data.id;
  const isRequester = project.requesterId === userId;
  const isBidder = project.bidderId === userId;
  if (!isRequester && !isBidder) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <div className="border-border bg-card/50 rounded-2xl border border-dashed p-10 text-center">
          <p className="text-muted-foreground text-sm">{t('error')}</p>
        </div>
      </div>
    );
  }

  const role: 'REQUESTER' | 'BIDDER' = isRequester ? 'REQUESTER' : 'BIDDER';
  const alreadyRated = ratingQuery.data?.callerSubmitted ?? false;
  const revealed = ratingQuery.data?.revealed ?? false;
  const received = isRequester
    ? ratingQuery.data?.bidderToRequester
    : ratingQuery.data?.requesterToBidder;

  return (
    <div className="flex flex-col gap-6">
      {header}

      {project.status !== 'COMPLETED' ? (
        <div className="border-border bg-card/50 rounded-2xl border border-dashed p-10 text-center">
          <p className="text-muted-foreground text-sm">{t('notCompleted')}</p>
        </div>
      ) : !alreadyRated ? (
        <section className="border-border bg-card rounded-2xl border p-6 shadow-[0_8px_24px_-12px_hsl(30_10%_30%/0.06)] md:p-8">
          <RatingForm projectId={project.id} role={role} />
        </section>
      ) : revealed && received ? (
        <ReceivedRating scores={received.scoresJson} comment={received.comment} role={role} />
      ) : (
        <div className="border-border bg-card/50 rounded-2xl border border-dashed p-10 text-center">
          <p className="text-muted-foreground text-sm">{t('alreadyRated')}</p>
        </div>
      )}
    </div>
  );
}
